/** Password login keeps its existing UI; its successful callback hands off to PKCE. */
const KEY = 'agora-fresh-session-v1';
const TTL_MS = 10 * 60 * 1000;
export const FRESH_SESSION_FLOW = 'flash-fresh-session-v1';

type PendingSession = {
  phase: 'password' | 'pkce';
  at: number;
  subject?: string;
  isNewUser?: boolean;
};

export type FreshSessionResult = {
  subject: string;
  isNewUser: boolean;
  hasRefreshToken: boolean;
};

// The result is written before Auth0Provider publishes its authenticated state.
// Keeping it on window makes it survive route remounts / duplicated entry chunks.
declare global {
  interface Window {
    __agoraFreshSessionResult?: FreshSessionResult;
    __agoraFreshSessionClaimed?: boolean;
  }
}

export const readPendingSession = (): PendingSession | null => {
  try {
    const value = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (
      value &&
      (value.phase === 'password' || value.phase === 'pkce') &&
      typeof value.at === 'number' &&
      Date.now() >= value.at &&
      Date.now() - value.at < TTL_MS &&
      (value.phase !== 'pkce' || (typeof value.subject === 'string' && value.subject.length > 0))
    ) {
      return value;
    }
  } catch {
    // No sessionStorage: retain the existing login flow.
  }
  return null;
};

export const clearPendingSession = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Storage access must not prevent an ordinary login.
  }
};

export const preparePasswordSession = (isNewUser = false): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ phase: 'password', at: Date.now(), isNewUser }));
  } catch {
    // The legacy login still works when storage is unavailable.
  }
};

export const beginFreshSession = (subject: string | undefined, isNewUser: boolean): boolean => {
  const pending = readPendingSession();
  if (!subject || pending?.phase !== 'password') return false;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ phase: 'pkce', at: Date.now(), subject, isNewUser: isNewUser || pending.isNewUser })
    );
    return true;
  } catch {
    return false;
  }
};

/** Query callbacks belong to the SPA SDK; legacy implicit hashes keep their parser. */
export const isAuthorizationCodeCallback = (pathname: string, search: string): boolean => {
  if (pathname.replace(/\/+$/, '') !== '/callback') return false;
  const params = new URLSearchParams(search);
  return params.has('state') && (params.has('code') || params.has('error'));
};

// This module is imported eagerly by GlobalProviders, before the SDK removes the
// code from the URL. The route itself is lazy and must not inspect the URL later.
export const isCodeCallbackAtBoot =
  typeof window !== 'undefined' &&
  isAuthorizationCodeCallback(window.location.pathname, window.location.search);

export const completeFreshSession = (
  appState: unknown,
  subject: string | undefined,
  hasRefreshToken: boolean
): 'complete' | 'mismatch' | 'unrelated' => {
  if (
    !appState ||
    typeof appState !== 'object' ||
    !('flow' in appState) ||
    appState.flow !== FRESH_SESSION_FLOW
  ) {
    return 'unrelated';
  }
  const pending = readPendingSession();
  clearPendingSession();
  if (!subject || pending?.phase !== 'pkce' || subject !== pending.subject) return 'mismatch';
  window.__agoraFreshSessionResult = { subject, isNewUser: Boolean(pending.isNewUser), hasRefreshToken };
  return 'complete';
};

export const claimFreshSessionResult = (): FreshSessionResult | null => {
  if (window.__agoraFreshSessionClaimed || !window.__agoraFreshSessionResult) return null;
  window.__agoraFreshSessionClaimed = true;
  return window.__agoraFreshSessionResult;
};
